import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
} from '@mui/material'
import { formatNumber, formatPercent } from '../../utils/format'
import { status as statusTokens } from '../../theme/tokens'

const MONTHS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar']

// -- Fallback demo data used when API returns empty/null --
const DEMO_DATA = [
  { budgetECs: 108000, planECs: 107850 },
  { budgetECs: 102000, planECs: 101925 },
  { budgetECs: 114000, planECs: 114225 },
  { budgetECs: 109000, planECs: 108600 },
  { budgetECs: 101000, planECs: 101775 },
  { budgetECs: 116000, planECs: 115725 },
  { budgetECs: 105000, planECs: 106350 },
  { budgetECs: 98000, planECs: 97875 },
  { budgetECs: 120000, planECs: 119850 },
  { budgetECs: 104000, planECs: 104400 },
  { budgetECs: 103000, planECs: 103200 },
  { budgetECs: 114000, planECs: 114225 },
]

function getVarianceColor(pct) {
  if (Math.abs(pct) <= 5) return statusTokens.ok
  return statusTokens.critical
}

export default function BudgetComparison({ data }) {
  const budgetData = data?.length ? data : DEMO_DATA // fallback to demo

  const ytdBudget = budgetData.reduce((s, d) => s + d.budgetECs, 0)
  const ytdPlan = budgetData.reduce((s, d) => s + d.planECs, 0)
  const ytdVariance = ytdPlan - ytdBudget
  const ytdVariancePct = (ytdVariance / ytdBudget) * 100

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <SummaryCard label="YTD Budget ECs" value={formatNumber(ytdBudget)} />
        <SummaryCard label="YTD Plan ECs" value={formatNumber(ytdPlan)} />
        <SummaryCard
          label="YTD Variance"
          value={`${ytdVariance >= 0 ? '+' : ''}${formatNumber(ytdVariance)} (${formatPercent(ytdVariancePct)})`}
          colorToken={getVarianceColor(ytdVariancePct)}
        />
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, minWidth: 80 }}>Month</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Budget ECs</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Plan ECs</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Variance</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Variance %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {budgetData.map((row, i) => {
              const variance = row.planECs - row.budgetECs
              const variancePct = (variance / row.budgetECs) * 100
              const vColor = getVarianceColor(variancePct)

              return (
                <TableRow key={MONTHS[i]} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{MONTHS[i]}</TableCell>
                  <TableCell align="right">{formatNumber(row.budgetECs)}</TableCell>
                  <TableCell align="right">{formatNumber(row.planECs)}</TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: vColor.color, fontWeight: 600 }}
                  >
                    {variance >= 0 ? '+' : ''}{formatNumber(variance)}
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={formatPercent(variancePct)}
                      size="small"
                      sx={{
                        backgroundColor: vColor.bg,
                        color: vColor.color,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        minWidth: 64,
                      }}
                    />
                  </TableCell>
                </TableRow>
              )
            })}

            <TableRow sx={{ '& td': { borderTop: '2px solid', borderTopColor: 'divider', fontWeight: 700 } }}>
              <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
              <TableCell align="right">{formatNumber(ytdBudget)}</TableCell>
              <TableCell align="right">{formatNumber(ytdPlan)}</TableCell>
              <TableCell align="right" sx={{ color: getVarianceColor(ytdVariancePct).color }}>
                {ytdVariance >= 0 ? '+' : ''}{formatNumber(ytdVariance)}
              </TableCell>
              <TableCell align="right">
                <Chip
                  label={formatPercent(ytdVariancePct)}
                  size="small"
                  sx={{
                    backgroundColor: getVarianceColor(ytdVariancePct).bg,
                    color: getVarianceColor(ytdVariancePct).color,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                  }}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

function SummaryCard({ label, value, colorToken }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: 2.5,
        py: 1.5,
        minWidth: 180,
        backgroundColor: colorToken?.bg || 'surface.containerLow',
      }}
    >
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography
        variant="h5"
        sx={{
          fontFamily: '"Inter", sans-serif',
          fontWeight: 700,
          color: colorToken?.color || 'text.primary',
        }}
      >
        {value}
      </Typography>
    </Paper>
  )
}
